import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  PaymentCredentials,
  PaymentCredentialsMeta,
  BankTransferConfig,
} from '@models';

type CallResult<T> = Promise<
  import('@angular/fire/functions').HttpsCallableResult<T>
>;

@Injectable({ providedIn: 'root' })
export class PaymentSettingsService {
  private readonly functions = inject(Functions);

  /** Saves manual credentials (Stripe keys / Redsys) and returns safe meta. */
  save(credentials: PaymentCredentials): CallResult<PaymentCredentialsMeta> {
    return httpsCallable<PaymentCredentials, PaymentCredentialsMeta>(
      this.functions,
      'savePaymentCredentials',
    )(credentials);
  }

  /** Returns metadata about stored credentials. Never returns actual secrets. */
  getMeta(): CallResult<PaymentCredentialsMeta | null> {
    return httpsCallable<void, PaymentCredentialsMeta | null>(
      this.functions,
      'getPaymentCredentialsMeta',
    )();
  }

  /** Saves or updates the bank transfer configuration. */
  saveBankTransferConfig(
    data: BankTransferConfig,
  ): CallResult<BankTransferConfig & { updatedAt: string }> {
    return httpsCallable<
      BankTransferConfig,
      BankTransferConfig & { updatedAt: string }
    >(
      this.functions,
      'saveBankTransferConfig',
    )(data);
  }

  /** Returns the bank transfer configuration, or null if not configured. */
  getBankTransferConfig(): CallResult<
    (BankTransferConfig & { updatedAt: string }) | null
  > {
    return httpsCallable<
      void,
      (BankTransferConfig & { updatedAt: string }) | null
    >(this.functions, 'getBankTransferConfig')();
  }

  /** Enables or disables the configured online payment gateway. */
  setGatewayEnabled(enabled: boolean): CallResult<{ gatewayEnabled: boolean }> {
    return httpsCallable<{ enabled: boolean }, { gatewayEnabled: boolean }>(
      this.functions,
      'setGatewayEnabled',
    )({ enabled });
  }

  /** Generate the Stripe Connect OAuth URL. The frontend then redirects to it. */
  stripeConnectOAuth(returnUrl: string): CallResult<{ url: string }> {
    return httpsCallable<{ returnUrl: string }, { url: string }>(
      this.functions,
      'stripeConnectOAuth',
    )({ returnUrl });
  }

  /** Disconnect (deauthorize) the Stripe Connect account for the current tenant. */
  stripeConnectDisconnect(): CallResult<{ success: boolean }> {
    return httpsCallable<void, { success: boolean }>(
      this.functions,
      'stripeConnectDisconnect',
    )();
  }

  /** Set the active mode (test/live) for a Stripe Connect tenant. */
  setStripeConnectMode(
    mode: 'test' | 'live',
  ): CallResult<{ activeMode: 'test' | 'live' }> {
    return httpsCallable<
      { mode: 'test' | 'live' },
      { activeMode: 'test' | 'live' }
    >(
      this.functions,
      'setStripeConnectMode',
    )({ mode });
  }
}
